import React, { useState, useEffect } from "react";
import {
  Flex,
  Button,
  Form,
  FormInput,
  RetryIcon,
  Tooltip,
  Text,
  FormButton,
  QuestionCircleIcon,
} from "@fluentui/react-northstar";
import Constants from "../../../../constants/text-constants";
import { observer } from "mobx-react-lite";
import PropTypes from "prop-types";
import AccountHandler from '../../../../services/api/accountHandler'
import RegExConst from "../../../../constants/regex";
import TextContentComponent from "../../TextContentComponent";
import VerifiedLabel from "../../../atoms/VerifiedLabel";
import PopupWithHeader from "../../../atoms/PopupWithHeader";

let originalEmailAddress = "";

const enableSaveButton = (changed, disabled) => {
  // enable if changed and not disabled
  return changed && !disabled;
};

const resendButtonFn = (emailVerified, renderResendButton) => {
  return emailVerified ? null : renderResendButton();
}

const resendFailedFn = (displayResendError, renderErrorMessage) => {
  return displayResendError ? renderErrorMessage() : null;
}

const TellUsAboutYouForm = observer((props) => {
  const {
    tellAboutYouOnboardComplete,
    firstName,
    lastName,
    emailAddress,
    emailVerified,
  } = props;

  const maxChars = {
    firstName: 50,
    lastName: 100,
    emailAddress: 260,
  };

  useEffect(() => {
    setState((prevState) => ({ ...prevState, firstName: firstName }));
  }, [firstName]);

  useEffect(() => {
    setState((prevState) => ({ ...prevState, lastName: lastName }));
  }, [lastName]);

  useEffect(() => {
    originalEmailAddress = emailAddress;
    setState((prevState) => ({ ...prevState, emailAddress: emailAddress }));
  }, [emailAddress]);

  useEffect(() => {
    setState((prevState) => ({ ...prevState, emailVerified: emailVerified }));
  }, [emailVerified]);

  const [state, setState] = useState({
    firstName: firstName,
    lastName: lastName,
    emailAddress: emailAddress,
    disableInputs: false,
    emailVerified: emailVerified,
    disableResendButton: false,
    displayResendError: false,
  });

  const [emailError, setEmailError] = useState(false);

  const onSave = (e) => {
    e.preventDefault();

    const regEx = new RegExp(RegExConst.EMAIL_VALIDATION);

    let valid = regEx.test(state.emailAddress);
    if (!valid) {
      setEmailError(true);
      return;
    }
    setState((prevState) => ({ ...prevState, disableInputs: true }));

    AccountHandler.setAccountProfile(state.firstName, state.lastName, state.emailAddress).then((response) => {
      const emailIsVerified = state.emailAddress !== emailAddress ? false : state.emailVerified;

      originalEmailAddress = state.emailAddress;
      setState((prevState) => ({ ...prevState, disableInputs: false, emailVerified: emailIsVerified }));

      if (tellAboutYouOnboardComplete) {
        tellAboutYouOnboardComplete({
          firstName: state.firstName,
          lastName: state.lastName,
          emailAddress: state.emailAddress,
          emailVerified: state.emailVerified,
        });
      }
    }).catch((err) => {
      console.warn("Error occured setting account profile");
      setState((prevState) => ({ ...prevState, disableInputs: false }));
    });
  }

  const changed =
    (emailAddress !== state.emailAddress) ||
    (firstName !== state.firstName) ||
    (lastName !== state.lastName);

  const disableButton = !enableSaveButton(
    changed,
    state.disableInputs,
  );

  const handleFieldChange = (e) => {
    let id = e.target.id;
    let value = e.target.value;

    setState((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleChangeEmailField = (e) => {
    setEmailError(false);
    setState((prevState) => ({ ...prevState, displayResendError: false }));
    handleFieldChange(e);
  };

  const renderResendButton = () => {
    return (
      <Tooltip
        content={Constants.RESEND_VERIFICATION_EMAIL}
        trigger={
          <Button
            primary
            onClick={onResendVerificationEmail}
            disabled={state.disableResendButton}
            iconOnly={true}
            text
            size="small"
            icon={<RetryIcon size="small" />}
            data-testid="resendEmailButton"
            type="button"
          />
        }
      />
    );
  }

  const renderErrorMessage = () => {
    return (<Text
      error
      size="small"
      data-testid="emailResendErrorText">
      {Constants.VERIFICATION_FAILED}
    </Text>);
  }

  const onResendVerificationEmail = () => {
    setState((prevState) => ({ ...prevState, disableResendButton: true, displayResendError: false }));

    AccountHandler.resendVerificationEmail()
      .then((response) => {
        // Do nothing - i.e. stay disabled
      }).catch((err) => {
        setState((prevState) => ({ ...prevState, disableResendButton: false, displayResendError: true }));
        console.warn("Error occured resending verification email");
      });
  }

  const getInputVerifiedLabel = (text) => {
    const { showEmailVerification } = props;
    let showVerified = false;

    // if we don't want to show the verification section at all
    // or if there is no email address saved.
    // if the email entered is the one saved, check whether verified or not
    if (showEmailVerification &&
      state.emailAddress.length !== 0 &&
      originalEmailAddress === state.emailAddress) {
      showVerified = true;
    }

    const resendButton = resendButtonFn(state.emailVerified, renderResendButton);
    const resendFailed = resendFailedFn(state.displayResendError, renderErrorMessage);

    return (
      <TextContentComponent
        text={text}
        component={showVerified ? (
          <Flex vAlign="center">
            <VerifiedLabel verified={state.emailVerified} className="verified-label" testId="Email" />
            {resendButton}
            {resendFailed}
          </Flex>
        ) : null}
        testId="verifedEmailTextComponent"
      />
    );
  };

  const popup = (
    <PopupWithHeader
      headerText={Constants.WHY_EMAIL_ADDRESS}
      trigger={
        <QuestionCircleIcon
          data-testid="tellUsAboutYouPopupTrigger"
          className="tell-us-about-you-email-icon"
          size="medium"
          outline />}
    >
      <Text>{Constants.WHY_EMAIL_ADDRESS_REASON1}</Text>
      <Text weight="bold">{Constants.WHY_EMAIL_ADDRESS_REASON2}</Text>
      <Text>{Constants.WHY_EMAIL_ADDRESS_REASON3}</Text>
    </PopupWithHeader>
  );

  return (
    <Flex
      fill
      column
      hAlign="center"
      className="full-width tcm-form"
      space="between"
    >
      <Form
        onSubmit={onSave}
        className="tell-us-about-you-form"
        data-testid="tellUsAboutYouForm"
      >
        <FormInput
          id="firstName"
          fluid
          data-testid="firstNameField"
          label={Constants.FIRSTNAME}
          value={state.firstName}
          onChange={handleFieldChange}
          disabled={state.disableInputs}
          maxLength={maxChars.firstName}
        />
        <FormInput
          id="lastName"
          fluid
          data-testid="lastNameField"
          label={Constants.LASTNAME}
          value={state.lastName}
          onChange={handleFieldChange}
          disabled={state.disableInputs}
          maxLength={maxChars.lastName}
        />
        <Flex className="email-address-input" gap="gap.smaller" column>
          { // This is not using the label field of the FormInput as it cause strange behaviour when you add
            // a button to the label. The hover-over behaviour activates randomly, and you can click it even
            // when you are not over the actual button
            getInputVerifiedLabel(Constants.EMAIL)
          }
          <Flex>
            <FormInput
              id="emailAddress"
              fluid
              data-testid="emailAddressField"
              value={state.emailAddress}
              onChange={handleChangeEmailField}
              error={emailError}
              errorMessage={emailError ? Constants.INVALID_EMAIL : ""}
              disabled={state.disableInputs}
              maxLength={maxChars.emailAddress}
            />
            {popup}
          </Flex>
        </Flex>
        {props.children}
        <FormButton
          primary
          loading={state.disableInputs}
          className="capitalize align-self-center"
          data-testid="tellUsABoutYouSaveButton"
          onClick={onSave}
          disabled={disableButton}
          content={Constants.SAVE}
        />
      </Form>
    </Flex>
  );
});

TellUsAboutYouForm.propTypes = {
  tellAboutYouOnboardComplete: PropTypes.func,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  emailAddress: PropTypes.string,
  emailVerified: PropTypes.bool,
  showEmailVerification: PropTypes.bool,
};

TellUsAboutYouForm.defaultProps = {
  firstName: "",
  lastName: "",
  emailAddress: "",
  emailVerified: false,
  showEmailVerification: false,
}

export default TellUsAboutYouForm;
